'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';

import { PageBlogOverviewSchema } from '@/sanity/pages/PageBlogOverviewTemplate';
import { resolveLinkUntracked } from '@/sanity/globals/CTA';
import { useBlogGrid } from './useBlogGrid';
import BlogCategoriesBar from './BlogCategoriesBar';
import BlogSearchInput from './BlogSearchInput';
import BlogCard from './BlogCard';

const ease = [0.25, 0.1, 0.25, 1] as const;

const ALL_LABEL: Record<string, string> = {
  en: 'All',
  de: 'Alle',
  br: 'Todos',
};
const SEARCH_PLACEHOLDER: Record<string, string> = {
  en: 'Search',
  de: 'Suchen',
  br: 'Buscar',
};
const LOAD_MORE_LABEL: Record<string, string> = {
  en: 'Load More',
  de: 'Mehr laden',
  br: 'Carregar mais',
};
const EMPTY_LABEL: Record<string, string> = {
  en: 'No posts found.',
  de: 'Keine Beiträge gefunden.',
  br: 'Nenhum post encontrado.',
};
const SCROLL_LEFT_LABEL: Record<string, string> = {
  en: 'Scroll categories left',
  de: 'Kategorien nach links scrollen',
  br: 'Rolar categorias para a esquerda',
};
const SCROLL_RIGHT_LABEL: Record<string, string> = {
  en: 'Scroll categories right',
  de: 'Kategorien nach rechts scrollen',
  br: 'Rolar categorias para a direita',
};

const containerWidth = 'max-lg:w-[calc(100%-4rem)] lg:w-[min(176.625rem,calc(100%-8rem))]';

export default function BlogGrid({
  data,
  language,
}: {
  data: PageBlogOverviewSchema;
  language: string;
}) {
  const {
    pills,
    visible,
    rest,
    hasMore,
    activeCategory,
    search,
    tabsRef,
    handleCategory,
    handleSearch,
    loadMore,
    scrollTabs,
  } = useBlogGrid(data.posts ?? [], data.categories ?? [], language);

  const allLabel = data.allLabel || ALL_LABEL[language] || ALL_LABEL.en;
  const searchPlaceholder =
    stegaClean(data.searchPlaceholder) || SEARCH_PLACEHOLDER[language] || SEARCH_PLACEHOLDER.en;
  const loadMoreLabel = data.loadMoreLabel || LOAD_MORE_LABEL[language] || LOAD_MORE_LABEL.en;
  const emptyLabel = data.emptyLabel || EMPTY_LABEL[language] || EMPTY_LABEL.en;
  const scrollLeftLabel = SCROLL_LEFT_LABEL[language] || SCROLL_LEFT_LABEL.en;
  const scrollRightLabel = SCROLL_RIGHT_LABEL[language] || SCROLL_RIGHT_LABEL.en;

  return (
    <section className="flex w-full flex-col items-center max-lg:pb-[10rem] lg:pb-[22.625rem]">
      <div
        className={`mx-auto flex ${containerWidth} max-lg:flex-col-reverse max-lg:gap-[1.75rem] lg:flex-row lg:items-center lg:justify-between lg:gap-[9.125rem]`}
      >
        <div className="min-w-0 max-lg:empty:hidden lg:flex-1">
          {pills.length > 0 && (
            <BlogCategoriesBar
              pills={pills}
              activeCategory={activeCategory}
              allLabel={allLabel}
              scrollLeftLabel={scrollLeftLabel}
              scrollRightLabel={scrollRightLabel}
              tabsRef={tabsRef}
              onSelect={handleCategory}
              onScrollLeft={() => scrollTabs('left')}
              onScrollRight={() => scrollTabs('right')}
            />
          )}
        </div>
        <BlogSearchInput value={search} onChange={handleSearch} placeholder={searchPlaceholder} />
      </div>

      {visible.length > 0 ? (
        <div
          className={`mx-auto grid ${containerWidth} items-stretch max-lg:mt-[5.875rem] max-lg:gap-[3rem] max-sm:grid-cols-1 sm:max-lg:grid-cols-2 lg:mt-[9rem] lg:grid-cols-3 lg:gap-x-[4rem] lg:gap-y-[5.125rem]`}
        >
          <AnimatePresence initial={false} mode="popLayout">
            {visible.map((post, i) => (
              <motion.div
                key={post._id}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8, transition: { duration: 0.2 } }}
                transition={{
                  duration: 0.4,
                  ease,
                  delay: Math.min((i % 9) * 0.05, 0.3),
                  layout: { duration: 0.35, ease },
                }}
              >
                <BlogCard post={post} language={language} titleAs="h2" preload={i === 0} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div
          className={`mx-auto flex ${containerWidth} items-center justify-center max-lg:py-[8rem] lg:py-[12rem]`}
        >
          <p className="mb-0 font-geist text-[2rem] tracking-[-0.06rem] text-black/50">
            {emptyLabel}
          </p>
        </div>
      )}

      {rest.length > 0 && (
        <ul hidden>
          {rest.map((post) => {
            const slug = stegaClean(post.slug);
            return slug ? (
              <li key={post._id}>
                <a href={resolveLinkUntracked(slug, language)}>{post.title}</a>
              </li>
            ) : null;
          })}
        </ul>
      )}

      <AnimatePresence initial={false}>
        {hasMore && (
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.4, ease }}
            onClick={loadMore}
            className="flex h-[5.75rem] items-center justify-center rounded-[0.25rem] bg-black px-[3.5rem] font-geist text-[1.75rem] font-semibold leading-[1.347] tracking-[-0.035rem] text-white transition-colors hover:bg-black/80 max-lg:mt-[3rem] max-lg:w-[calc(100%-4rem)] lg:mt-[6.375rem] lg:w-fit"
          >
            {loadMoreLabel}
          </motion.button>
        )}
      </AnimatePresence>
    </section>
  );
}
